import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import {
  getSessionStorage,
  loadFromStorageKeyPrefix,
  persistSectionPrefixed,
} from "./reduxStorageCommon"

export enum AuthenticationSource {
  DevEmail = "dev_email",
  Token = "investor_token",

  // New assessment flow methods
  AssessmentGoogle = "assessment_google",
  AssessmentLinkedIn = "assessment_linkedin",
  AssessmentGithub = "assessment_github",
  AssessmentEmail = "assessment_email",

  // New solana flow methods  
  // SolanaGoogle = "solana_google",
  // SolanaGithub = "solana_github",
  // SolanaCoinbase = "solana_coinbase",
}

// Extract the authentication flow from AuthenticationSource string
export const getAuthFlow = (authSource: AuthenticationSource): string => {
  const sourceStr = authSource as string;
  return sourceStr.split('_')[0];
};

// Extract the authentication method from AuthenticationSource string
export const getAuthMethod = (authSource: AuthenticationSource): string => {
  const sourceStr = authSource as string;
  return sourceStr.split('_')[1];
};

interface AuthPreferencesState {
  authenticationSource?: AuthenticationSource;
  subClientMode: boolean;
  subClientModeAuthorized: boolean;
}

const AuthPrefsPrefix = "authPrefs_";

const getStorage = () => {
  return getSessionStorage();
};

const loadFromStorage = (key: string) => {
  return loadFromStorageKeyPrefix(key, AuthPrefsPrefix, getStorage());
};

const initialState: AuthPreferencesState = {
  authenticationSource: loadFromStorage('authenticationSource'),
  subClientMode: loadFromStorage('subClientMode') ?? false,
  subClientModeAuthorized: loadFromStorage('subClientModeAuthorized') ?? false,
};

export const authPreferencesSlice = createSlice({
  name: 'authPreferences',
  initialState,
  reducers: {
    setAuthenticationSource: (state, action: PayloadAction<AuthenticationSource>) => {
      state.authenticationSource = action.payload;
    },
    setSubClientMode: (state, action: PayloadAction<boolean>) => {
      state.subClientMode = action.payload;
    },
    setSubClientModeAuthorized: (state, action: PayloadAction<boolean>) => {
      state.subClientModeAuthorized = action.payload;
    },
    clearAuthPreferences: (state) => {
      state.authenticationSource = undefined;
      state.subClientMode = false;
      state.subClientModeAuthorized = false;
    }
  }
});

export const {
  setAuthenticationSource,
  setSubClientMode,
  setSubClientModeAuthorized,
  clearAuthPreferences
} = authPreferencesSlice.actions;

export const selectUserAuthSource = (state: RootState) => state.authPreferences.authenticationSource;
export const selectSubClientMode = (state: RootState) => state.authPreferences.subClientMode;
export const selectSubClientModeAuthorized = (state: RootState) => state.authPreferences.subClientModeAuthorized;

// Persistence setup
const persistAuthPreferences = (authPrefsData: AuthPreferencesState) => {
  persistSectionPrefixed(AuthPrefsPrefix, authPrefsData, getStorage());
};

export const setupAuthPreferencesPersistence = (store: any) => {
  store.subscribe(() => {
    const authPrefsState = store.getState().authPreferences;
    persistAuthPreferences(authPrefsState);
  });
};

export default authPreferencesSlice.reducer;
